import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import CampaignsController from '../controllers/CampaignsController';
import { CampaignsOverviewController } from '../controllers/CampaignsOverviewController';
import { CtmAdController } from '../controllers/CtmAdController';
import { MessengerAdController } from '../controllers/MessengerAdController';
import RssFeedController from '../controllers/RssFeedController';
import SponsoredMessageController from '../controllers/SponsoredMessageController';
import commonHandler from '../errorHandlers/common-handler';
import {
  CampaignOverviewKindType,
  CampaignsOverviewPayload,
} from '../model/CampaignOverviewModel';
import { StoreState } from '../store-interfaces';
import CrudActionContainer from './CrudActionContainer';

type GetAllParamsType = Parameters<typeof CampaignsOverviewController.getAll>;

export class CampaignsOverviewCRUDActions extends CrudActionContainer {
  static NAME = 'CAMPAIGNS_OVERVIEW';
  static retrieveAllSuccess = 'CAMPAIGNS_OVERVIEW_RETRIEVED_SUCCESS';
  static retrieveEnabledInputTriggersSuccess =
    'ENABLED_INPUT_TRIGGERS_RETRIEVED_SUCCESS';
  static invalidateAll = 'CAMPAIGNS_OVERVIEW_INVALIDATE';
  static deleteSuccess = 'CAMPAIGNS_OVERVIEW_DELETE_SUCCESS';

  constructor() {
    super(CampaignsOverviewCRUDActions.NAME);
  }

  retrieveAll(...params: GetAllParamsType) {
    const { botId, kind, mergeResults } = params[0];

    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const payload = await CampaignsOverviewController.getAll(...params);

        const retrieveAllSuccessAction: RetrievedAllSuccessActionType = {
          type: CampaignsOverviewCRUDActions.retrieveAllSuccess,
          botId,
          kind,
          payload: {
            ...payload,
            campaigns: payload.campaigns,
          },
          mergeResults,
        };
        dispatch(retrieveAllSuccessAction);

        return retrieveAllSuccessAction;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  async retrieveAutomationCounters(botId: number) {
    try {
      const response =
        await CampaignsOverviewController.retrieveAutomationCounters(botId);

      return response;
    } catch (e) {
      return { total: 0, active: 0 };
    }
  }

  retrieveEnabledInputTriggers(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const payload =
          await CampaignsOverviewController.retrieveEnabledInputTriggers(botId);

        const retrieveSuccessAction: RetrievedInputTriggersSuccessActionType = {
          type: CampaignsOverviewCRUDActions.retrieveEnabledInputTriggersSuccess,
          botId,
          payload: {
            ...payload,
            enabledriggers: payload.enabledriggers,
          },
        };
        dispatch(retrieveSuccessAction);
        return retrieveSuccessAction;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  deleteByKindAndId(
    botId: number,
    kind: CampaignOverviewKindType,
    entityId: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        switch (kind) {
          case 'legacy_messenger_ads':
            await MessengerAdController.deleteMessengerAd(botId, entityId);
            break;
          case 'sponsored':
            await SponsoredMessageController.deleteSponsoredMessage(
              botId,
              entityId,
            );
            break;
          case 'rss':
            await RssFeedController.deleteRssFeed(botId, entityId);
            break;
          case 'ctm_ads':
            await CtmAdController.deleteCtmAd(botId, entityId);
            break;
          case 'blaster':
          case 'drip':
            await CampaignsController.deleteCampaign(entityId);
            break;
          default:
            throw Error(`Delete for kind "delete${kind}" is not implemented`);
        }

        const deleteEntitySuccessAction: DeleteSuccessActionType = {
          type: CampaignsOverviewCRUDActions.deleteSuccess,
          payload: {
            botId,
            kind,
            entityId,
          },
        };
        dispatch(deleteEntitySuccessAction);
      } catch (err) {
        return commonHandler(dispatch)(err);
      }
    };
  }

  invalidateAll() {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: CampaignsOverviewCRUDActions.invalidateAll });
    };
  }
}

export type RetrievedAllSuccessActionType = {
  type: typeof CampaignsOverviewCRUDActions.retrieveAllSuccess;
  botId: number;
  kind: CampaignOverviewKindType;
  payload: CampaignsOverviewPayload;
  mergeResults?: boolean;
};

export type RetrievedInputTriggersSuccessActionType = {
  type: typeof CampaignsOverviewCRUDActions.retrieveEnabledInputTriggersSuccess;
  botId: number;
  payload: {
    enabledTriggers: string[];
  };
};

export type DeleteSuccessActionType = {
  type: typeof CampaignsOverviewCRUDActions.deleteSuccess;
  payload: {
    botId: number;
    kind: CampaignOverviewKindType;
    entityId: number;
  };
};

export const campaignsOverviewCRUDActions = new CampaignsOverviewCRUDActions();

export const useCampaignsOverviewCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      retrieveAll: (...params: GetAllParamsType) =>
        dispatch(campaignsOverviewCRUDActions.retrieveAll(...params)),
      retrieveEnabledInputTriggers: (botId: number) =>
        dispatch(
          campaignsOverviewCRUDActions.retrieveEnabledInputTriggers(botId),
        ),
      invalidateAll: () =>
        dispatch(campaignsOverviewCRUDActions.invalidateAll()),
      deleteByKindAndId: (
        ...params: Parameters<
          typeof campaignsOverviewCRUDActions.deleteByKindAndId
        >
      ) => dispatch(campaignsOverviewCRUDActions.deleteByKindAndId(...params)),
    }),
    [dispatch],
  );
};
